const FOCUSABLE_SELECTOR = 'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex]'
const RANGE_MODES = ['daterange', 'datetimerange', 'timerange']

// Moves focus to the field before/after `element` within its own form. The picker
// overlay is mounted on document.body, so it never shows up among the candidates.
function focusNeighbour(element, backwards) {
  const scope = element.closest('form') || document
  const fields = Array.prototype.slice.call(scope.querySelectorAll(FOCUSABLE_SELECTOR)).filter((field) => {
    return !field.disabled && field.tabIndex >= 0 && field.getClientRects().length > 0
  })

  const index = fields.indexOf(element)
  if (index < 0) return

  const neighbour = fields[index + (backwards ? -1 : 1)]
  if (neighbour) neighbour.focus()
}

// Modes whose input text is a single value in a format we render ourselves, so it
// can be read back reliably. The range modes are left out: they hold an array
// rather than one value, and `daterange` writes its own input text in `onSet`.
const SEEDABLE_MODES = ['date', 'datetime', 'time', 'month']

// A month field reads "Dezember 2025" — the month name of the picker's locale,
// which `MonthPickerInput` renders through I18n to match. Read it back with the
// picker's own names so the two cannot drift apart again.
function parseMonth(text, locale) {
  const match = /^\s*(\S+)\s+(\d{4})\s*$/.exec(text || '')
  if (!match) return null

  const pack = window.DateTimePicker.getLocale(locale)
  const names = pack && pack.monthNames
  if (!names) return null

  const index = names.findIndex((name) => name.toLowerCase() === match[1].toLowerCase())
  return index < 0 ? null : new Date(+match[2], index, 1)
}

// The picker constructor starts on `_value = null` and never looks at the value
// the server rendered into the input, so every field it is attached to claims to
// be empty until the user picks something. Parse that text back into a Date so
// the picker starts out knowing what the field actually holds.
//
// Without this the picker opens on today instead of on the stored value, and —
// worse — `prefillToday` below saw `getVal()` as empty and overwrote the date the
// moment the field was opened or merely focused.
function parseValue(text, mode, locale) {
  if (mode === 'month') return parseMonth(text, locale)

  const date = /(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(text || '')
  const time = /(\d{1,2}):(\d{2})/.exec(text || '')
  if (!date && mode !== 'time') return null
  if (!date && !time) return null

  const value = date ? new Date(+date[3], +date[2] - 1, +date[1]) : new Date()
  // Reject dates the calendar would silently roll over, e.g. "31.02.2025"
  if (date && (value.getDate() !== +date[1] || value.getMonth() !== +date[2] - 1)) return null

  value.setHours(time ? +time[1] : 0, time ? +time[2] : 0, 0, 0)
  return isNaN(value.getTime()) ? null : value
}

// An empty date field starts out on today, so a single click is enough for the common case.
// `element.value` is checked alongside the picker's own value: a field the picker
// could not parse is still a field that holds a date, and must not be overwritten.
function prefillToday(picker, element) {
  if (picker.getVal() || element.value) return

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const options = picker.getActiveOptions()
  if (options.min && today < options.min) return
  if (options.max && today > options.max) return
  if (typeof options.disabled === 'function' && options.disabled(today)) return

  picker.setVal(today)
  return true
}

up.compiler('[data-picker-mode]', (element) => {
  const mode = element.dataset.pickerMode
  const options = {
    mode: mode,
    locale: element.dataset.pickerLocale || 'de',
    display: element.dataset.pickerDisplay || 'anchored',
    // ZETAS has no dark mode — never follow the OS color scheme
    theme: 'light',
  }

  // Format overrides
  if (element.dataset.pickerDateFormat) options.dateFormat = element.dataset.pickerDateFormat
  if (element.dataset.pickerTimeFormat) options.timeFormat = element.dataset.pickerTimeFormat

  // Constraints
  if (element.dataset.pickerStepMinute) options.stepMinute = parseInt(element.dataset.pickerStepMinute)
  if (element.dataset.pickerCalendarMonths) options.calendarMonths = parseInt(element.dataset.pickerCalendarMonths)
  if (element.dataset.pickerMin) options.min = new Date(element.dataset.pickerMin)
  if (element.dataset.pickerMax) options.max = new Date(element.dataset.pickerMax)

  // Month picker: restrict to allowed months
  if (element.dataset.pickerAllowedMonths) {
    const allowed = new Set(JSON.parse(element.dataset.pickerAllowedMonths))
    options.disabled = (date) => {
      const key = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0')
      return !allowed.has(key)
    }
  }

  // Daterange: predefined presets (built into the picker)
  if (mode === 'daterange' && element.dataset.pickerPresets !== 'false') {
    const today = () => new Date()
    const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d }
    const startOfMonth = () => { const d = new Date(); d.setDate(1); return d }
    const endOfMonth = () => { const d = new Date(); d.setMonth(d.getMonth() + 1, 0); return d }
    const startOfLastMonth = () => { const d = new Date(); d.setMonth(d.getMonth() - 1, 1); return d }
    const endOfLastMonth = () => { const d = new Date(); d.setDate(0); return d }
    const startOfYear = () => new Date(new Date().getFullYear(), 0, 1)
    const endOfYear = () => new Date(new Date().getFullYear(), 11, 31)
    const startOfLastYear = () => new Date(new Date().getFullYear() - 1, 0, 1)
    const endOfLastYear = () => new Date(new Date().getFullYear() - 1, 11, 31)

    options.presets = [
      { label: 'Letzte 7 Tage',    value: () => [daysAgo(6), today()] },
      { label: 'Letzten 30 Tage',  value: () => [daysAgo(29), today()] },
      { label: 'Letzten 365 Tage', value: () => [daysAgo(365), today()] },
      { label: 'Dieser Monat',     value: () => [startOfMonth(), endOfMonth()] },
      { label: 'Letzter Monat',    value: () => [startOfLastMonth(), endOfLastMonth()] },
      { label: 'Dieses Jahr',      value: () => [startOfYear(), endOfYear()] },
      { label: 'Letztes Jahr',     value: () => [startOfLastYear(), endOfLastYear()] },
    ]
  }

  const notifiesUnpoly = element.dataset.pickerTimeless !== 'true'
  const notifyUnpoly = () => element.dispatchEvent(new Event('change', { bubbles: true }))
  let muted = false

  // Daterange writes its own input value — onSet runs before onChange, so the
  // formatted value is in place by the time Unpoly picks the field up.
  options.onSet = (e) => {
    if (mode === 'daterange') {
      const fmt = (d) => [d.getDate(), d.getMonth()+1, d.getFullYear()].map(n => String(n).padStart(2,'0')).join('.')
      element.value = fmt(e.value[0]) + ' - ' + fmt(e.value[1])
    }
  }

  // Value change → Unpoly validation. This has to hang off onChange rather than
  // onSet: a time typed into the input commits via setVal, which only fires onChange.
  options.onChange = () => {
    if (notifiesUnpoly && !muted) {
      notifyUnpoly()
    }
  }

  // Tab leaves the open picker instead of cycling through the overlay: it commits the
  // selection and lands on the next field — for "Von"/"Bis" that is the time input,
  // which takes typed digits right away. Range pickers keep their internal Tab
  // navigation, since they have presets and a toolbar to reach.
  let tabHandler = null

  const stopTabHandler = () => {
    if (!tabHandler) return
    document.removeEventListener('keydown', tabHandler, true)
    tabHandler = null
  }

  let prefilled = false

  options.onOpen = (e) => {
    // Muted: validating on open would re-render the field and tear the overlay
    // down again. The prefilled date reaches the form on close instead.
    muted = true
    prefilled = mode === 'date' && prefillToday(e.inst, element) === true
    muted = false

    if (RANGE_MODES.includes(mode)) return

    tabHandler = (event) => {
      if (event.key !== 'Tab') return
      event.preventDefault()
      e.inst.close()
      focusNeighbour(element, event.shiftKey)
    }
    document.addEventListener('keydown', tabHandler, true)
  }

  options.onClose = stopTabHandler

  options.onCancel = () => {
    // Daterange: clear on cancel
    if (mode === 'daterange') {
      element.value = ''
      notifyUnpoly()
      return
    }
    // A prefilled date survives the cancel, so the form still has to hear about it
    if (prefilled && notifiesUnpoly) {
      notifyUnpoly()
    }
  }

  const picker = new window.DateTimePicker(element, options)
  element.dtpInstance = picker

  // Hand the picker the value the server rendered. Muted: this is not a user
  // edit, so it must not kick off an Unpoly validation.
  if (SEEDABLE_MODES.includes(mode)) {
    const initial = parseValue(element.value, mode, options.locale)
    if (initial) {
      muted = true
      picker.setVal(initial)
      muted = false
    }
  }

  return () => {
    stopTabHandler()
    picker.destroy()
  }
})
